/**
 * Centralne URL-mapowanie dla assetów gry.
 *
 * Vite serwuje `assets/` jako publicDir → URL ścieżki = ścieżki na dysku.
 * Po reorganizacji (sesja 25c) layout:
 *
 *   assets/
 *   ├── biomes/<biomeFolder>/
 *   │   ├── environment/    — tło + dekoracje (deco_*, bg_*)
 *   │   ├── destructibles/  — niszczone obiekty
 *   │   └── pets/           — pety biom-specific
 *   ├── events/<eventId>/   — content czasowy (przyszłe eventy)
 *   ├── avatars/            — shared, gracz skin
 *   ├── eggs/               — shared, jajka (typ jest globalny)
 *   ├── ui/                 — shared, ikony przycisków
 *   └── title/              — shared, title splash
 *
 * Konwencja id biomu → folder: prefix `biome-` zdejmowany. Np.
 *   'biome-meadow'   → 'meadow'
 *   'biome-mountain' → 'mountain'
 *
 * Wszystkie miejsca w kodzie (PreloadScene, modal'e wyświetlające PNG-i)
 * MUSZĄ używać tych helperów zamiast hardcoded ścieżek — dzięki temu zmiana
 * layoutu = jeden plik do edycji.
 */

import type { ID } from '@/domain/types/common';
import { BIOMES_BY_ID } from '@/config/biomes.config';

type ImageAssetFormat = 'webp' | 'png';

/**
 * Public assety z `assets/` nie są hashowane przez Vite, a produkcyjny nginx może
 * trzymać je w cache. Zmieniaj tę wartość po podmianie grafiki pod tą samą nazwą.
 */
const PUBLIC_ASSET_VERSION = '2026-08-18-school-blackboard-67-v1';

function versionedPublicAssetUrl(url: string, version = PUBLIC_ASSET_VERSION): string {
  return `${url}?v=${version}`;
}

/** 'biome-meadow' → 'meadow'. Używane do budowy ścieżek w `/biomes/<folder>/`. */
export function biomeFolder(biomeId: ID): string {
  return biomeId.replace(/^biome-/, '');
}

/**
 * Bazowy segment ścieżki assetów biomu. Biom EVENTOWY (`eventAssetFolder` z
 * `BiomeDef`) → `/events/<folder>` (`assets/events/<folder>/`); zwykły biom →
 * `/biomes/<folder>`. Jedno miejsce decyduje o routingu — każdy przyszły event
 * (Halloween, Zima) tylko ustawia `eventAssetFolder` w swoim biomie.
 */
function biomeAssetBase(biomeId: ID, eventAssetFolder?: string): string {
  // Gdy caller nie poda jawnie folderu eventu, doliczamy go z definicji biomu
  // (`BiomeDef.eventAssetFolder`). Dzięki temu event-pety routują do
  // `/events/<folder>/` w KAŻDYM miejscu — także tam, gdzie komponent nie zna
  // folderu (modal wyklucia, karta peta w ekwipunku, merge). Jawny argument wygrywa.
  const folder = eventAssetFolder ?? BIOMES_BY_ID[biomeId]?.eventAssetFolder;
  return folder ? `/events/${folder}` : `/biomes/${biomeFolder(biomeId)}`;
}

// ─── BIOME-SPECIFIC ASSETS ──────────────────────────────────────────────────
// Wszystkie przyjmują opcjonalny `eventAssetFolder` (z `BiomeDef`) — gdy podany,
// asset jest ładowany z `assets/events/<folder>/...` zamiast `assets/biomes/...`.

/** Pet image URL. Każdy pet należy do jednego biomu (origin.biomeId). */
export function petAssetUrl(textureKey: string, biomeId: ID, eventAssetFolder?: string): string {
  return versionedPublicAssetUrl(`${biomeAssetBase(biomeId, eventAssetFolder)}/pets/${textureKey}.webp`);
}

/** Destructible image URL. */
export function destructibleAssetUrl(textureKey: string, biomeId: ID, eventAssetFolder?: string): string {
  return versionedPublicAssetUrl(`${biomeAssetBase(biomeId, eventAssetFolder)}/destructibles/${textureKey}.webp`);
}

/** Background tile / dekoracja URL (folder `environment/`). */
export function biomeEnvironmentAssetUrl(
  textureKey: string,
  biomeId: ID,
  eventAssetFolder?: string,
  format: ImageAssetFormat = 'webp',
): string {
  return versionedPublicAssetUrl(`${biomeAssetBase(biomeId, eventAssetFolder)}/environment/${textureKey}.${format}`);
}

/** Ambient FX particle URL (folder `fx/`). Per-biom: snow w mountain,
 *  przyszłe popiół w wulkanie, liście w lesie itd. */
export function biomeFxAssetUrl(textureKey: string, biomeId: ID, eventAssetFolder?: string): string {
  return versionedPublicAssetUrl(`${biomeAssetBase(biomeId, eventAssetFolder)}/fx/${textureKey}.webp`);
}

/** Ambient critter sprite URL (folder `critters/`). Dekoracyjne stworzonka tła
 *  (motyle, ptaki, tumbleweed) animowane ruchem w kodzie. Transparent sticker. */
export function biomeCritterAssetUrl(textureKey: string, biomeId: ID, eventAssetFolder?: string): string {
  return versionedPublicAssetUrl(`${biomeAssetBase(biomeId, eventAssetFolder)}/critters/${textureKey}.webp`);
}

/** Obrazek zabytku (biom edukacyjny, folder `landmarks/`). Pokazywany w karcie
 *  „Zabytek" w EduInteractBar. Per-biom, jak pozostałe assety biomu. */
export function biomeLandmarkAssetUrl(textureKey: string, biomeId: ID): string {
  return versionedPublicAssetUrl(`/biomes/${biomeFolder(biomeId)}/landmarks/${textureKey}.webp`);
}

// ─── SHARED ASSETS (poza biomami) ───────────────────────────────────────────

export function eggAssetUrl(textureKey: string): string {
  return versionedPublicAssetUrl(`/eggs/${textureKey}.webp`);
}

/** Plakat albumu eventu (folder `events/<eventFolder>/album/`). EV-3 Album graficzny. */
export function eventAlbumPosterUrl(eventFolder: string, textureKey: string): string {
  return versionedPublicAssetUrl(`/events/${eventFolder}/album/${textureKey}.webp`);
}

/**
 * Plakat peta z albumu Banku (BANK-A3) — nagroda za komplet naklejek.
 * Pliki w `assets/bank/posters/<defId>.webp`, generowane PARTIAMI (najpierw
 * Łąka) — brak pliku jest NORMALNY: klient łapie `onError` i pokazuje
 * placeholder „Grafika wkrótce!" (odblokowanie w DB jest niezależne od
 * istnienia grafiki, DESIGN_BANK.md §4.4).
 */
export function bankPosterUrl(defId: ID): string {
  return versionedPublicAssetUrl(`/bank/posters/${defId}.webp`);
}

export function avatarAssetUrl(textureKey: string): string {
  // Szkolne skiny podmieniono pod dotychczasowymi ID; nowy URL odświeża cache
  // tylko tych dwóch grafik, bez ponownego pobierania wszystkich assetów gry.
  const version = textureKey === 'avatar_album_toilet_boy' || textureKey === 'avatar_album_toilet_girl'
    ? '2026-09-26-school-uniform-v1'
    : PUBLIC_ASSET_VERSION;
  return versionedPublicAssetUrl(`/avatars/${textureKey}.webp`, version);
}

// UI icons używają bezpośrednio `/ui/<key>.webp` w Icon component (który
// normalizuje końcówkę na `.webp`) — nie ma helpera bo UI nie ma żadnej
// dynamicznej wariacji (zawsze ten sam URL).
// Gdyby kiedyś zaszła potrzeba (np. event dodał własne UI), dodaj
// eventUiAssetUrl(eventId, key) tu.
